import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { policyApi } from "../../lib/api";
import { PolicyDemoBanner } from "../../components/PolicyMode";

export function PolDrafts() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [title, setTitle] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [policyTitle, setPolicyTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = useAsync(
    async () => {
      const [drafts, policies] = await Promise.all([policyApi.drafts(orgId), policyApi.policies(orgId)]);
      return { drafts, policies };
    },
    [orgId],
  );

  const drafts = state.data?.drafts.data.drafts ?? [];
  const policies = state.data?.policies.data.policies ?? [];

  async function createPolicy() {
    if (!policyTitle.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await policyApi.createPolicy(orgId, { title: policyTitle.trim() });
      setPolicyTitle("");
      state.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the policy.");
    } finally {
      setSaving(false);
    }
  }

  async function createDraft() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await policyApi.createDraft(orgId, {
        title: title.trim(),
        ...(policyId ? { policyId } : {}),
      });
      setTitle("");
      state.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the draft.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Policy authoring</h1>
          <p>Draft policies, cut immutable versions, and route them for governed review.</p>
        </div>
      </div>

      <PolicyDemoBanner show={state.data?.drafts.demoMode ?? false} />
      {error && <ErrorState description={error} />}

      <Card>
        <CardHeader title="Policy registry" />
        <CardBody>
          <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
            <Field label="New policy title">
              {(id) => (
                <TextInput
                  id={id}
                  value={policyTitle}
                  placeholder="Information Security Policy"
                  onChange={(e) => setPolicyTitle(e.target.value)}
                />
              )}
            </Field>
            <Button size="sm" onClick={createPolicy} disabled={saving || !policyTitle.trim()}>
              Add policy
            </Button>
          </div>
          {policies.length > 0 && (
            <ul className="ods-stack" style={{ marginTop: "var(--ods-space-3)", paddingLeft: 0, listStyle: "none" }}>
              {policies.map((p) => (
                <li key={p.id} className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                  <strong style={{ fontWeight: 600 }}>{p.title}</strong>
                  <Badge tone="neutral">{p.status}</Badge>
                  {p.referenceCode ? <span style={{ color: "var(--ods-color-muted)" }}>{p.referenceCode}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="New draft" />
        <CardBody>
          <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
            <Field label="Draft title">
              {(id) => (
                <TextInput
                  id={id}
                  value={title}
                  placeholder="Information Security Policy v3"
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>
            <Field label="Policy">
              {(id) => (
                <Select id={id} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
                  <option value="">Unbound</option>
                  {policies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button size="sm" onClick={createDraft} disabled={saving || !title.trim()}>
              Create draft
            </Button>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading drafts…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && drafts.length === 0 && (
        <EmptyState title="No drafts yet" description="Create a draft to start authoring a policy." />
      )}
      {drafts.length > 0 && (
        <Card>
          <CardHeader title="Drafts" />
          <CardBody>
            <ul className="ods-stack" style={{ paddingLeft: 0, listStyle: "none" }}>
              {drafts.map((d) => (
                <li key={d.id} className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "center" }}>
                  <Link to={`/studio/policy-ai/drafts/${d.id}`}>
                    <strong style={{ fontWeight: 600 }}>{d.title}</strong>
                  </Link>
                  <Badge tone="neutral">{d.status}</Badge>
                  <span style={{ color: "var(--ods-color-muted)" }}>{formatDate(d.updatedAt)}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}
    </>
  );
}
